import { BsArrowBarDown, BsArrowBarUp } from 'react-icons/bs';
import { Card } from 'react-bootstrap';
import { FC, useState } from 'react';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer, typeMode } from 'src/store';
import { Hr } from 'src/components/_commons';
import { typeRole, typeUser } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetCurrentUser, useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';
import { UserAssignmentsModal } from '../../_commons/UserAssignmentsModal';
import { UsersChangeEmailModal } from './UsersChangeEmailModal';
import { userServices } from 'src/services';
import { UsersPersonalDataModal } from './UsersPersonalDataModal';

type propsType = {
    currentUser: typeUser;
    users: typeUser[];
};

export const UsersCard: FC<propsType> = ({ currentUser, users }) => {
    const [showAssignments, setShowAssignments] = useState(false);
    const [showCardBody, setShowCardBody] = useState(false);
    const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
    const [showPersonalDataModal, setShowPersonalDataModal] = useState(false);
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();
    const user = useGetCurrentUser();

    const openAlertModalHandler = (mode: typeMode, title: string, message: string, animation?: number, execution?: Function): void => {
        dispatch(setValuesAndOpenAlertModalReducer({
            mode,
            title,
            message,
            execution,
            animation
        }));
    }

    const openResetPasswordConfirmModalHandler = (): void => {
        openAlertModalHandler(
            'confirm',
            '¿Resetear clave?',
            `Esto reseteará la contraseña del usuario ${currentUser.email}, cerrará sus sesiones y le enviará un correo con la nueva contraseña`,
            undefined,
            async () => {
                dispatch(showLoadingModalReducer());
                const response = await userServices.ChangePswOtherUser(currentUser.email);
                dispatch(hideLoadingModalReducer());
                if (!response.success || !response.data?.newPassword) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                openAlertModalHandler('alert', `Clave reseteada y enviada por email a ${currentUser.email}`, `Nueva clave: ${response.data.newPassword}`, 1);
            }
        );
    }

    const openDeleteUserConfirmModalHandler = (): void => {
        if (currentUser.id === user.id) {
            return;
        }
        const isAdmin = !!currentUser.roles.length || !!currentUser.isSuperAdmin;
        if (isAdmin) {
            openAlertModalHandler(
                'alert',
                'No se puede eliminar a un administrador',
                `Para eliminar al usuario ${currentUser.name} ${currentUser.lastName} (${currentUser.email}) hay que primero removerle los roles`,
                undefined
            );
            return;
        }
        const isActive = currentUser.isActive;
        if (isActive) {
            openAlertModalHandler(
                'alert',
                'No se puede eliminar a un usuario activo',
                `Para eliminar al usuario ${currentUser.name} ${currentUser.lastName} (${currentUser.email}) hay que desactivarlo primero`,
                undefined
            );
            return;
        }
        openAlertModalHandler(
            'confirm',
            '¿Eliminar este usuario?',
            `Se eliminará el usuario ${currentUser.email} definitivamente`,
            3,
            async () => {
                dispatch(showLoadingModalReducer());
                const response = await userServices.Delete(currentUser.id);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                openAlertModalHandler(
                    'alert',
                    'Usuario eliminado',
                    `Se eliminó al usuario ${currentUser.email}`,
                    1
                );
                window.location.reload();
            }
        );
    }

    const editUserHandler = async (isActive: boolean, roles: typeRole[]): Promise<void> => {
        dispatch(showLoadingModalReducer());
        const response = await userServices.Edit(currentUser.id, isActive, roles);
        dispatch(hideLoadingModalReducer());
        dispatch(setAlertResponse({ response }));
    }

    return (
        <Card key={currentUser.email}
            className={isDarkMode ? 'bg-dark text-white' : ''}
            style={{
                backgroundColor: '#f6f6f8',
                margin: '30px auto 0 auto',
                width: isMobile ? '95%': '500px'
            }}
        >
            {showAssignments &&
                <UserAssignmentsModal
                    closeModal={() => setShowAssignments(false)}
                    currentUser={currentUser}
                    users={users}
                />
            }
            {showChangeEmailModal &&
                <UsersChangeEmailModal
                    closeModal={() => setShowChangeEmailModal(false)}
                    currentUser={currentUser}
                />
            }
            {showPersonalDataModal &&
                <UsersPersonalDataModal
                    closeModal={() => setShowPersonalDataModal(false)}
                    currentUser={currentUser}
                />
            }
            <Card.Body style={{ padding: `20px 30px ${showCardBody ? '30px' : '12px'} 30px` }}>
                <Card.Title className={'pointer text-center mb-0'}
                    onClick={() => setShowCardBody(x => !x)}
                >
                    <div style={{ fontSize: isMobile ? '1.3rem' : '1.8rem' }}>
                        {currentUser.name} {currentUser.lastName}
                    </div>
                    <div className={'mt-2'} style={{ fontSize: isMobile ? '1rem' : '1.3rem' }}>
                        {currentUser.email}
                    </div>
                    {showCardBody ?
                        <BsArrowBarUp size={isMobile ? '2rem' : '2.4rem'} className={'mt-3'} />
                        :
                        <BsArrowBarDown size={isMobile ? '2rem' : '2.4rem'} className={'mt-3'} />
                    }
                </Card.Title>
                {showCardBody &&
                    <>
                    <Hr />
                    <button className={'col-12 btn btn btn-general-blue my-1'}
                        onClick={() => setShowAssignments(true)}
                    >
                        VER ASIGNACIONES
                    </button>
                    {user.isAdmin &&  // no sense... only admins can see this page
                        <>
                        <button className={'col-12 btn btn btn-general-red my-1'}
                            onClick={() => setShowPersonalDataModal(true)}
                        >
                            EDITAR DATOS PERSONALES
                        </button>
                        <br/>
                        {user?.email !== currentUser.email &&
                            <button className={'col-12 btn btn-general-red my-1'}
                                onClick = {() => setShowChangeEmailModal(true)}
                            >
                                CAMBIAR EMAIL
                            </button>
                        }
                        {user?.email !== currentUser.email &&
                            <button className={'col-12 btn btn-general-red my-1'}
                                onClick = {openResetPasswordConfirmModalHandler}
                            >
                                RESETEAR CONTRASEÑA
                            </button>
                        }
                        <button className={`col-12 btn btn ${currentUser.isActive ? 'btn-general-red' : 'btn-general-blue'} my-1`}
                            onClick={() => editUserHandler(!currentUser.isActive, currentUser.roles)}
                        >
                            {currentUser.isActive ? 'DESACTIVAR' : 'ACTIVAR'}
                        </button>
                        <button className={'col-12 btn btn-general-red my-1'}
                            onClick = {openDeleteUserConfirmModalHandler}
                            disabled={currentUser.id === user.id}
                        >
                            ELIMINAR USUARIO
                        </button>
                        <Hr />
                        <button className={`col-12 btn ${currentUser.roles.includes(1) ? 'btn-general-red' : 'btn-general-blue'} my-1`}
                            onClick = {() => editUserHandler(currentUser.isActive, currentUser.roles.includes(1) ? currentUser.roles.filter(r => r !== 1) : [1])}
                        >
                            {currentUser.roles.includes(1) ? 'QUITAR COMO ADMIN GENERAL' : 'HACER ADMIN GENERAL'}
                        </button>
                        <br />
                        <button className={`col-12 btn ${currentUser.roles.includes(2) ? 'btn-general-red' : 'btn-general-blue'} my-1`}
                            onClick = {() => editUserHandler(currentUser.isActive, currentUser.roles.includes(2) ? currentUser.roles.filter(r => r !== 2) : [...currentUser.roles.filter(r => r !== 1 && r !== 5), 2])}
                            disabled={currentUser.roles.includes(1)}
                        >
                            {currentUser.roles.includes(2) ? 'QUITAR COMO ADMIN DE CASA EN CASA' : 'HACER ADMIN DE CASA EN CASA'}
                        </button>
                        <button className={`col-12 btn ${currentUser.roles.includes(3) ? 'btn-general-red' : 'btn-general-blue'} my-1`}
                            onClick = {() => editUserHandler(currentUser.isActive, currentUser.roles.includes(3) ? currentUser.roles.filter(r => r !== 3) : [...currentUser.roles.filter(r => r !== 1), 3])}
                            disabled={currentUser.roles.includes(1)}
                        >
                            {currentUser.roles.includes(3) ? 'QUITAR COMO ADMIN DE TELEFÓNICA' : 'HACER ADMIN DE TELEFÓNICA'}
                        </button>
                        <button className={`col-12 btn ${currentUser.roles.includes(4) ? 'btn-general-red' : 'btn-general-blue'} my-1`}
                            onClick = {() => editUserHandler(currentUser.isActive, currentUser.roles.includes(4) ? currentUser.roles.filter(r => r !== 4) : [...currentUser.roles.filter(r => r !== 1), 4])}
                            disabled={currentUser.roles.includes(1)}
                        >
                            {currentUser.roles.includes(4) ? 'QUITAR COMO ADMIN DE CARRITOS' : 'HACER ADMIN DE CARRITOS'}
                        </button>
                        <button className={`col-12 btn ${currentUser.roles.includes(5) ? 'btn-general-red' : 'btn-general-blue'} my-1`}
                            onClick = {() => editUserHandler(currentUser.isActive, currentUser.roles.includes(5) ? currentUser.roles.filter(r => r !== 5) : [...currentUser.roles.filter(r => r !== 1 && r !== 2), 5])}
                            disabled={currentUser.roles.includes(1)}
                        >
                            {currentUser.roles.includes(5) ? 'QUITAR COMO ADMIN DE EDIFICIOS' : 'HACER ADMIN DE EDIFICIOS'}
                        </button>
                        <button className={`col-12 btn ${currentUser.roles.includes(6) ? 'btn-general-red' : 'btn-general-blue'} my-1`}
                            onClick = {() => editUserHandler(currentUser.isActive, currentUser.roles.includes(6) ? currentUser.roles.filter(r => r !== 6) : [...currentUser.roles.filter(r => r !== 1), 6])}
                            disabled={currentUser.roles.includes(1)}
                        >
                            {currentUser.roles.includes(6) ? 'QUITAR COMO ADMIN DE TABLERO' : 'HACER ADMIN DE TABLERO'}
                        </button>
                        <button className={`col-12 btn ${currentUser.roles.includes(7) ? 'btn-general-red' : 'btn-general-blue'} my-1`}
                            onClick = {() => editUserHandler(currentUser.isActive, currentUser.roles.includes(7) ? currentUser.roles.filter(r => r !== 7) : [...currentUser.roles.filter(r => r !== 1), 7])}
                            disabled={currentUser.roles.includes(1)}
                        >
                            {currentUser.roles.includes(7) ? 'QUITAR COMO ADMIN DE REUNIONES' : 'HACER ADMIN DE REUNIONES'}
                        </button>
                        </>
                    }
                    </>
                }
            </Card.Body>
        </Card>
    );
}
